import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingsAPI } from '../services/api';
import { format } from 'date-fns';
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Eye,
  Users,
  Clock,
  MapPin,
  RefreshCw,
  MessageSquare
} from 'lucide-react';
import MapPinButton from '../components/MapPinButton';
import toast from 'react-hot-toast';

const Bookings = () => {
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    travel_date_from: '',
    travel_date_to: ''
  });

  const queryClient = useQueryClient();

  // Fetch bookings
  const { data: bookingsData, isLoading, error } = useQuery({
    queryKey: ['bookings', filters],
    queryFn: () => bookingsAPI.getAll(filters)
  });

  const bookings = bookingsData?.data?.data?.bookings || [];

  // Mutations for booking actions
  const confirmMutation = useMutation({
    mutationFn: (id) => {
      console.log('Confirming booking ID:', id);
      return bookingsAPI.confirm(id);
    },
    onSuccess: (response) => {
      console.log('Confirm success:', response);
      queryClient.invalidateQueries(['bookings']);
      queryClient.invalidateQueries(['booking-stats']);
      toast.success('Booking confirmed successfully');
    },
    onError: (error) => {
      console.error('Confirm error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to confirm booking');
    }
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => bookingsAPI.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['bookings']);
      queryClient.invalidateQueries(['booking-stats']);
      toast.success('Booking cancelled successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to cancel booking');
    }
  });

  const sendSMSMutation = useMutation({
    mutationFn: (id) => bookingsAPI.sendConfirmationSMS(id),
    onSuccess: (response) => {
      const phone = response.data?.data?.phone;
      toast.success(`Confirmation SMS sent successfully${phone ? ` to ${phone}` : ''}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Failed to send confirmation SMS');
    }
  });

  const handleConfirm = (booking) => {
    console.log('handleConfirm called for:', booking);
    if (window.confirm(`Confirm booking ${booking.booking_reference}?`)) {
      console.log('User confirmed, calling mutate...');
      confirmMutation.mutate(booking.id);
    } else {
      console.log('User cancelled confirmation');
    }
  };

  const handleCancel = (booking) => {
    if (window.confirm(`Cancel booking ${booking.booking_reference}? This action cannot be undone.`)) {
      cancelMutation.mutate(booking.id);
    }
  };

  const handleSendSMS = (booking) => {
    if (!booking.customer_phone) {
      toast.error('No phone number available for this customer');
      return;
    }
    if (window.confirm(`Send confirmation SMS to ${booking.customer_phone}?`)) {
      sendSMSMutation.mutate(booking.id);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="badge-confirmed">Confirmed</span>;
      case 'pending':
        return <span className="badge-pending">Pending</span>;
      case 'cancelled':
        return <span className="badge-cancelled">Cancelled</span>;
      default:
        return <span className="badge bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      travel_date_from: '',
      travel_date_to: ''
    });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries(['bookings']);
    toast.success('Bookings refreshed');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage bus bookings and confirmations
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="search" className="form-label">
              Search
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="search"
                name="search"
                type="text"
                className="form-input pl-10"
                placeholder="Search bookings..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="status" className="form-label">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="form-input"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label htmlFor="travel_date_from" className="form-label">
              From Date
            </label>
            <input
              id="travel_date_from"
              name="travel_date_from"
              type="date"
              className="form-input"
              value={filters.travel_date_from}
              onChange={(e) => setFilters({ ...filters, travel_date_from: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="travel_date_to" className="form-label">
              To Date
            </label>
            <input
              id="travel_date_to"
              name="travel_date_to"
              type="date"
              className="form-input"
              value={filters.travel_date_to}
              onChange={(e) => setFilters({ ...filters, travel_date_to: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={clearFilters}
            className="btn-outline"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <Filter className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Reference</th>
                  <th className="table-header">Customer</th>
                  <th className="table-header">Route</th>
                  <th className="table-header">Travel Date</th>
                  <th className="table-header">Time</th>
                  <th className="table-header">Passengers</th>
                  <th className="table-header">Price</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td className="table-cell">
                      <div className="text-sm font-mono text-gray-900">
                        {booking.booking_reference}
                      </div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(booking.created_at), 'MMM d, h:mm a')}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.customer_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {booking.customer_email || booking.customer_phone}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-900">
                          <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                          {booking.origin} → {booking.destination}
                        </div>
                        {(booking.pickup_location || booking.dropoff_location) && (
                          <div className="flex flex-wrap gap-2">
                            {booking.pickup_location && (
                              <MapPinButton 
                                address={booking.pickup_location} 
                                label="Pickup"
                                size="xs"
                                variant="primary"
                              />
                            )}
                            {booking.dropoff_location && (
                              <MapPinButton 
                                address={booking.dropoff_location} 
                                label="Drop-off"
                                size="xs"
                                variant="secondary"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-900">
                        {format(new Date(booking.travel_date), 'MMM d, yyyy')}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="h-4 w-4 text-gray-400 mr-1" />
                        {booking.departure_time}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center text-sm text-gray-900">
                        <Users className="h-4 w-4 text-gray-400 mr-1" />
                        {booking.total_passengers}
                      </div>
                      <div className="text-xs text-gray-500">
                        A:{booking.num_adults} C:{booking.num_children} S:{booking.num_seniors}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        ${parseFloat(booking.total_price).toFixed(2)}
                      </div>
                    </td>
                    <td className="table-cell">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleConfirm(booking)}
                              disabled={confirmMutation.isLoading}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                              title="Confirm booking"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Confirm
                            </button>
                            <button
                              onClick={() => handleCancel(booking)}
                              disabled={cancelMutation.isLoading}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                              title="Cancel booking"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancel
                            </button>
                          </>
                        )}

                        {booking.status === 'confirmed' && (
                          <>
                            {booking.customer_phone && (
                              <button
                                onClick={() => handleSendSMS(booking)}
                                disabled={sendSMSMutation.isLoading}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                title="Send confirmation SMS"
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                Send SMS
                              </button>
                            )}
                            <button
                              onClick={() => handleCancel(booking)}
                              disabled={cancelMutation.isLoading}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                              title="Cancel booking"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancel
                            </button>
                          </>
                        )}

                        <button
                          className="text-gray-600 hover:text-gray-900"
                          title="View details"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;